import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import { FaUsers, FaPlus, FaTrash } from "react-icons/fa";

const RoleManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(false);

  // Get auth headers
  const getHeaders = () => {
    const token = localStorage.getItem("token") || (user && user.token);
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Load roles
  const loadRoles = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/roles`, getHeaders());
      if (res.data.success) {
        setRoles(res.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch roles");
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  // Create role
  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRole.name) {
      toast.error("Please enter a role name");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/roles`, newRole, getHeaders());
      if (res.data.success) {
        toast.success(res.data.message || "Role created successfully!");
        setNewRole({ name: "", description: "" });
        loadRoles();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  // Delete role
  const handleDeleteRole = async (roleId, roleName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the role "${roleName}"? This will also remove all its permissions.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${API_URL}/admin/roles/${roleId}`, getHeaders());
        if (res.data.success) {
          toast.success(res.data.message || "Role deleted!");
          loadRoles();
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to delete role");
      }
    }
  };

  return (
    <div className="w-full py-8">
      <div className="px-4 sm:px-6 md:px-8 max-w-full mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Create Role Form */}
          <div className="lg:col-span-1 bg-white border border-gray-100 rounded-2xl shadow-sm p-6 self-start">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2 pb-3 border-b border-gray-50">
              <FaPlus className="text-blue-500 text-sm" /> Create Custom Role
            </h2>
            <form onSubmit={handleCreateRole} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Role Name
                </label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-gray-50/50"
                  placeholder="e.g. employee"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                  Description
                </label>
                <textarea
                  rows={4}
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-gray-50/50"
                  placeholder="Describe what tasks this role performs"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm tracking-wide shadow-lg shadow-blue-200 transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? "CREATING..." : "CREATE ROLE"}
              </button>
            </form>
          </div>

          {/* Roles Table */}
          <div className="lg:col-span-2 bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Custom Roles Directory</h2>
              <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-bold">
                {roles.length} roles total
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-100/50 border-b border-gray-200/60">
                    <th className="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Role Name</th>
                    <th className="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="py-3 px-6 text-xs font-bold text-gray-400 uppercase tracking-wider">Created Date</th>
                    <th className="py-3 px-6 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {roles.length > 0 ? (
                    roles.map((r) => (
                      <tr key={r._id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-6">
                          <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-extrabold tracking-wide uppercase">
                            {r.name}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600 font-medium">
                          {r.description || <span className="text-gray-300 italic">No description provided</span>}
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-500 font-medium">
                          {new Date(r.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <button
                            onClick={() => handleDeleteRole(r._id, r.name)}
                            className="p-2 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors inline-flex items-center justify-center"
                            title="Delete Role"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="py-8 px-6 text-center text-gray-400 italic text-sm" colSpan="4">
                        No custom roles found. Set up a role on the left panel!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
